import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * BookingClosedNotice (Jose 2026-10-13) — until the Oct-19 cutover, SLIK is NOT the booking system yet
 * (the shop still runs on Fresha), so a SLIK booking would be orphaned. The blast drove people here to
 * CREATE ACCOUNTS, but many tried to BOOK and got confused. This replaces the time-picker with a clear
 * "book on Fresha until the 19th" message. Auto-lifts at launch — `isBookingOpen()` flips on Oct 19.
 */
export const BOOKING_LAUNCH = new Date(2026, 9, 19, 0, 0, 0); // LIVE 2026-10-19 — booking opened on go-live night (gate now in the past = open for all clients)

// ── OWNER KILL SWITCH ── publicConfig/bookingPause (toggled from staff Settings). When paused, ALL online
// booking stops instantly: this page shows the paused notice + getAvailableSlots offers nothing. Kept in
// sync by the self-subscribing listener at the bottom of this file; defaults to NOT paused on load/error
// so a legit booking is NEVER blocked by accident.
let _bookingPaused = false;
let _bookingPauseMsg = '';
export const setBookingPaused = (paused: boolean, message?: string) => { _bookingPaused = !!paused; _bookingPauseMsg = message || ''; };
export const isBookingPaused = () => _bookingPaused;
export const bookingPauseMessage = () => _bookingPauseMsg;

export const isBookingOpen = () => !_bookingPaused && Date.now() >= BOOKING_LAUNCH.getTime();

function useCountdown(target: Date) {
  const calc = () => {
    const ms = target.getTime() - Date.now();
    return ms <= 0 ? { d: 0, h: 0, m: 0 } : { d: Math.floor(ms / 86400000), h: Math.floor((ms % 86400000) / 3600000), m: Math.floor((ms % 3600000) / 60000) };
  };
  const [t, setT] = useState(calc);
  useEffect(() => { const i = setInterval(() => setT(calc()), 30000); return () => clearInterval(i); }, []);
  return t;
}

export const BookingClosedNotice: React.FC = () => {
  const { d, h, m } = useCountdown(BOOKING_LAUNCH);
  if (isBookingPaused()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2A211A] to-[#15181d] flex items-center justify-center p-6 text-center">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-5xl mb-3">⏸️</div>
          <h1 className="text-2xl font-extrabold text-slik-navy">Online booking is paused</h1>
          <p className="mt-4 text-base text-gray-700 leading-relaxed">
            {bookingPauseMessage() || "We've briefly paused online booking. Please call the shop to book — we'll be right back."}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2A211A] to-[#15181d] flex items-center justify-center p-6 text-center">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8">
        <div className="text-5xl mb-3">🗓️</div>
        <h1 className="text-2xl font-extrabold text-slik-navy">SLIK booking opens <span className="text-slik-purple">October&nbsp;19</span></h1>
        <div className="mt-3 inline-flex items-center gap-2 bg-slik-purple/10 text-slik-purple rounded-full px-4 py-1.5 font-extrabold">
          <span>{d}d</span><span className="opacity-50">:</span><span>{h}h</span><span className="opacity-50">:</span><span>{m}m</span>
          <span className="ml-1 text-xs font-semibold uppercase opacity-70">to go</span>
        </div>
        <p className="mt-5 text-base text-gray-700 leading-relaxed">
          Your account is all set — nice! 🎉 Our barbers are still on <b>Fresha</b> until the 19th, so please
          <b> keep booking on Fresha</b> the way you always have, just for now.
        </p>
        <p className="mt-3 text-base text-gray-700 leading-relaxed">
          On <b>October&nbsp;19</b>, come right back here and book SLIK <b>directly</b>. Same shop, same barbers —
          we can't wait to see you.
        </p>
      </div>
    </div>
  );
};

// Self-subscribing singleton — keep the kill-switch flag in sync for the whole app the moment any booking
// surface imports this module. Default NOT paused on error (never block legit booking by accident).
try {
  onSnapshot(doc(db, 'publicConfig', 'bookingPause'),
    (s) => { const d = s.data() as any; setBookingPaused(!!(d && d.paused), d?.message); },
    () => setBookingPaused(false));
} catch { /* ignore */ }

export default BookingClosedNotice;
